<template>
  <div :class="['shift', isUnclaimed(props.shift) ? 'has-border' : '']" @click="navigateToShift()">
    <div class="details-row">
      <div class="date">
        <span class="day">{{ formattedDate.dayName }}</span>
        <span class="day-num">{{ formattedDate.dayNumber }}</span>
        <span class="month">{{ formattedDate.month }}</span>
      </div>
      <div class="info">
        <div class="meta">
          <div class="role-row">
            <span class="role">{{ props.shift.portal_role_name }}</span>
            <span v-if="props.shift?.properties?.type" class="trial-shift">{{
              props.shift?.properties?.type === 'trial_shift' ? 'Trial shift' : 'Interview'
            }}</span>
          </div>
          <span>{{ formattedTimeRange }}</span>
        </div>
        <div class="price">
          <span v-if="props.shift.client_rate && props.shift.hours" class="total"
            >£{{ toRawCurrency(props.shift.client_rate * props.shift.hours) }}</span
          >
          <div class="completed" v-if="isCompleted(props.shift)">
            <CheckCircleFilled :color="PaletteColors['success']" width="15px" height="15px"></CheckCircleFilled>
            Completed
          </div>
          <div class="no-show" v-if="isNoShow(props.shift)">
            <Cross :color="PaletteColors['danger']" width="15px" height="15px"></Cross>
            No show
          </div>
          <div class="happening-today" v-if="isFulfilled(props.shift) && isToday(props.shift.start_at)">
            <Clock :color="PaletteColors['brand-primary']" width="15px" height="15px"></Clock>Today
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Cross from '@/assets/icons/Cross.vue';
import Clock from '@/assets/icons/Clock.vue';
import CheckCircleFilled from '@/assets/icons/CheckCircleFilled.vue';
import { formatDateSegments, formatTimeRange } from '@/pages/talent-pool/utils/datetime';
import { toRawCurrency } from '@/utils';
import { useRouter } from 'vue-router/composables';
import { isCompleted, isFulfilled, isUnclaimed, isNoShow } from '@/pages/talent-pool/utils/ShiftStatus';
import { PaletteColors } from '@/Theme';
import { isToday } from 'date-fns';

const props = defineProps({
  shift: Object,
});

const router = useRouter();

const navigateToShift = () => {
  router
    .push({
      name: 'talent-pool-shift',
      params: { shiftId: props.shift.shift_id, locationId: props.shift.location_id },
    })
    .catch(() => {});
};

// Use the function to get the formatted time range
const formattedTimeRange = formatTimeRange(props.shift.start_at, props.shift.end_at);

// Use the function to get the formatted date
const formattedDate = formatDateSegments(props.shift.start_at);
</script>

<style scoped lang="scss">
.shift {
  padding: 1rem 1rem 1rem 0.75rem;
  background: var(--palette-color-base-white);
  display: flex;
  flex-direction: column;
  color: var(--palette-color-default);
  transition: 0.2s;
  width: 100%;
  justify-content: center;

  &:hover {
    cursor: pointer;
    border-color: var(--palette-color-default-lighten-80);
    box-shadow: 0px 0px 2px 0px rgba(34, 40, 47, 0.05), 0px 2px 10px 0px rgba(34, 40, 47, 0.1);
  }
}

.shift.has-border {
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  box-shadow: 0px 2px 1px 0px rgba(34, 40, 47, 0.05), 0px 0px 3px 0px rgba(34, 40, 47, 0.1);
}

.details-row {
  display: flex;
  align-items: center;
}

.date {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-right: 1.5rem;

  &:after {
    position: absolute;
    right: 0.75rem;
    top: 0;
    bottom: 0;
    margin: auto;
    display: block;
    content: '';
    width: 1px;
    height: 1.5rem;
    background: var(--palette-color-default-lighten-90);
  }
}

.date .day-num {
  font-size: 23px;
  line-height: 20px;
  font-weight: 600;
}

.button-width {
  width: max-content;
}

.date .day,
.date .month {
  font-size: 10px;
  font-weight: 300;
}

.info {
  display: flex;
  flex-grow: 1;
  font-weight: 300;
  font-size: 14px;
  gap: 0.5rem;
  min-width: 0;
}

.info .meta {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.info .meta .role-row {
  display: flex;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: 0.25rem;
}

.info .price {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.01em;
}

.info .role {
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 500;
}

.trial-shift {
  background-color: var(--palette-color-default-lighten-90);
  border-radius: 40px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.02em;
  white-space: nowrap;
}

.worker-row {
  margin-top: 0.5rem;
  padding-left: 3.25rem;

  .info {
    display: flex;
    gap: 0.375rem;
    border-radius: 0.5rem;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.02em;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
  }

  .no-claims {
    color: var(--palette-color-default-lighten-30);
    background-color: var(--palette-color-default-lighten-95);
  }

  .select-worker {
    color: var(--palette-color-brand-primary);
    background-color: var(--palette-color-core-1-lighten-90);
    border: 1px solid var(--palette-color-brand-primary);

    svg {
      width: 1rem;
      height: 1rem;
      fill: var(--palette-color-brand-primary);
    }
  }
}

.completed {
  color: var(--palette-color-success);
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.no-show {
  color: var(--palette-color-danger);
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.happening-today {
  color: var(--palette-color-brand-primary);
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
